/**
 * Generated by orval v8.11.0 🍺
 * Do not edit manually.
 * Core API
 * Core API Documentation
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  CreateEmergencyLightCheckDto,
  CreateEmergencyLightCheckResponseDto,
  EmergencyLightCheckDetailResponseDto,
  ListEmergencyLightChecksQueryControllerListParams,
  ListEmergencyLightChecksResponseDto,
} from "../model";

import { axiosClient } from "../../lib/api";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Records either the monthly flick test or the daily/weekly visual check against every registered light. Every light in service when the check was done must be included — a partial set is rejected so no light can be skipped (AC13). A check replayed with `capturedAt` is judged against the register at that time. The light name, room name and room id are snapshotted at submit time. The response carries the created item ids so a failed light can be deep-linked straight into the create-follow-up flow.
 * @summary Submit an emergency lighting check
 */
export const createEmergencyLightCheckCommandControllerCreate = (
  nurseryId: string,
  createEmergencyLightCheckDto: CreateEmergencyLightCheckDto,
  signal?: AbortSignal,
) => {
  return axiosClient<CreateEmergencyLightCheckResponseDto>({
    url: `/v1/nurseries/${nurseryId}/emergency-light-checks`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: createEmergencyLightCheckDto,
    signal,
  });
};

export const getCreateEmergencyLightCheckCommandControllerCreateMutationOptions =
  <TError = void, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>
      >,
      TError,
      { nurseryId: string; data: CreateEmergencyLightCheckDto },
      TContext
    >;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>
    >,
    TError,
    { nurseryId: string; data: CreateEmergencyLightCheckDto },
    TContext
  > => {
    const mutationKey = ["createEmergencyLightCheckCommandControllerCreate"];
    const { mutation: mutationOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey } };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>
      >,
      { nurseryId: string; data: CreateEmergencyLightCheckDto }
    > = (props) => {
      const { nurseryId, data } = props ?? {};

      return createEmergencyLightCheckCommandControllerCreate(nurseryId, data);
    };

    return { mutationFn, ...mutationOptions };
  };

export type CreateEmergencyLightCheckCommandControllerCreateMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>>
  >;
export type CreateEmergencyLightCheckCommandControllerCreateMutationBody =
  CreateEmergencyLightCheckDto;
export type CreateEmergencyLightCheckCommandControllerCreateMutationError =
  void;

/**
 * @summary Submit an emergency lighting check
 */
export const useCreateEmergencyLightCheckCommandControllerCreate = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>
      >,
      TError,
      { nurseryId: string; data: CreateEmergencyLightCheckDto },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>>,
  TError,
  { nurseryId: string; data: CreateEmergencyLightCheckDto },
  TContext
> => {
  return useMutation(
    getCreateEmergencyLightCheckCommandControllerCreateMutationOptions(options),
    queryClient,
  );
};
/**
 * Newest first. Pass `kind` to keep the flick test and the visual check as separate histories (AC11).
 * @summary List emergency lighting checks for a nursery (History tab)
 */
export const listEmergencyLightChecksQueryControllerList = (
  nurseryId: string,
  params?: ListEmergencyLightChecksQueryControllerListParams,
  signal?: AbortSignal,
) => {
  return axiosClient<ListEmergencyLightChecksResponseDto>({
    url: `/v1/nurseries/${nurseryId}/emergency-light-checks`,
    method: "GET",
    params,
    signal,
  });
};

export const getListEmergencyLightChecksQueryControllerListQueryKey = (
  nurseryId: string,
  params?: ListEmergencyLightChecksQueryControllerListParams,
) => {
  return [
    `/v1/nurseries/${nurseryId}/emergency-light-checks`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListEmergencyLightChecksQueryControllerListQueryOptions = <
  TData = Awaited<
    ReturnType<typeof listEmergencyLightChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListEmergencyLightChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListEmergencyLightChecksQueryControllerListQueryKey(nurseryId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>
  > = ({ signal }) =>
    listEmergencyLightChecksQueryControllerList(nurseryId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: nurseryId !== null && nurseryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListEmergencyLightChecksQueryControllerListQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>
  >;
export type ListEmergencyLightChecksQueryControllerListQueryError = void;

export function useListEmergencyLightChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listEmergencyLightChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params: undefined | ListEmergencyLightChecksQueryControllerListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listEmergencyLightChecksQueryControllerList>
          >,
          TError,
          Awaited<
            ReturnType<typeof listEmergencyLightChecksQueryControllerList>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListEmergencyLightChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listEmergencyLightChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListEmergencyLightChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listEmergencyLightChecksQueryControllerList>
          >,
          TError,
          Awaited<
            ReturnType<typeof listEmergencyLightChecksQueryControllerList>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListEmergencyLightChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listEmergencyLightChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListEmergencyLightChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List emergency lighting checks for a nursery (History tab)
 */

export function useListEmergencyLightChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listEmergencyLightChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListEmergencyLightChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listEmergencyLightChecksQueryControllerList>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getListEmergencyLightChecksQueryControllerListQueryOptions(
      nurseryId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get one emergency lighting check with its per-light breakdown
 */
export const getEmergencyLightCheckQueryControllerGet = (
  emergencyLightCheckId: string,
  signal?: AbortSignal,
) => {
  return axiosClient<EmergencyLightCheckDetailResponseDto>({
    url: `/v1/emergency-light-checks/${emergencyLightCheckId}`,
    method: "GET",
    signal,
  });
};

export const getGetEmergencyLightCheckQueryControllerGetQueryKey = (
  emergencyLightCheckId: string,
) => {
  return [`/v1/emergency-light-checks/${emergencyLightCheckId}`] as const;
};

export const getGetEmergencyLightCheckQueryControllerGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
  TError = void,
>(
  emergencyLightCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetEmergencyLightCheckQueryControllerGetQueryKey(emergencyLightCheckId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>
  > = ({ signal }) =>
    getEmergencyLightCheckQueryControllerGet(emergencyLightCheckId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      emergencyLightCheckId !== null && emergencyLightCheckId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmergencyLightCheckQueryControllerGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>
>;
export type GetEmergencyLightCheckQueryControllerGetQueryError = void;

export function useGetEmergencyLightCheckQueryControllerGet<
  TData = Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
  TError = void,
>(
  emergencyLightCheckId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetEmergencyLightCheckQueryControllerGet<
  TData = Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
  TError = void,
>(
  emergencyLightCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
          TError,
          Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetEmergencyLightCheckQueryControllerGet<
  TData = Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
  TError = void,
>(
  emergencyLightCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get one emergency lighting check with its per-light breakdown
 */

export function useGetEmergencyLightCheckQueryControllerGet<
  TData = Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
  TError = void,
>(
  emergencyLightCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getEmergencyLightCheckQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetEmergencyLightCheckQueryControllerGetQueryOptions(
    emergencyLightCheckId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}
